import React, { useState,useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { 
  Receipt as ReceiptIcon,
  Timeline as TimelineIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { api } from '../../../../services/api';
import { cardSx,headerSx,
  buttonGenerateInvoiceSx,
  buttonDownloadPdfSx,
  buttonSendInvoiceSx,
  cardContentSx,
  alertErrorSx,
  alertSuccessSx,
  statusBoxSx,
  chipSx,
  loadingBoxSx } from '../../styles/TripStatus.styles';
  import { TripStatusSectionProps } from '../../../../types/tripStatus';
import { formatDateTime } from '../../../../utils/dateFormatter';

const STATUS_OPTIONS = [
  { value: 'HOT LEAD', label: 'Hot Lead', color: 'error' },
  { value: 'COLD LEAD', label: 'Cold Lead', color: 'info' },
  { value: 'PENDING', label: 'Pending', color: 'warning' },
  { value: 'CONFIRMED', label: 'Confirmed', color: 'info' },
  { value: 'DRIVER ASSIGNED', label: 'Driver Assigned', color: 'secondary' },
  { value: 'IN PROGRESS', label: 'In Progress', color: 'primary' },
  { value: 'COMPLETED', label: 'Completed', color: 'success' },
  { value: 'IN CANCELLATION', label: 'In Cancellation', color: 'warning' },
  { value: 'CLOSED', label: 'Closed', color: 'error' },
  { value: 'AUDITED', label: 'Audited', color: 'success' },
  { value: 'JUNK', label: 'Junk', color: 'error' },
] as const;

const TripStatusSection: React.FC<TripStatusSectionProps> = ({
  tripCode,
  updatedAt,
  currentStatus,
  loading = false,
  onStatusUpdate,
  onSendInvoice,
  onGenerateInvoice,
  onDownloadInvoicePdf,
  onRefreshTripDetails,
  isAdmin = false,
  isAccountant,
  hasDriverAssigned = false,
  hasVehicleAssigned = false,
  hasFeedback = false,
  hasPendingPayments = false,
  hasPendingRefunds = false,
  totalPaid =0,
  amountDue =0,
  transactions =[],
  pickupDatetime,
  disabled = false,
}) => {
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [generatedInvoiceId, setGeneratedInvoiceId] = useState<string | null>(null);
  const [zohoErrorDialogOpen, setZohoErrorDialogOpen] = useState(false);

  const getCurrentStatusInfo = () => {
    return STATUS_OPTIONS.find(option => option.value === currentStatus) || 
           { value: currentStatus, label: currentStatus, color: 'default' as const };
  };

  const isWithinThreeHoursOfPickup = () => 
  pickupDatetime && (new Date(pickupDatetime).getTime() - Date.now()) <= 3 * 60 * 60 * 1000;
  // && new Date(pickupDatetime).getTime() >= Date.now();

  const handleStatusUpdate = async (newStatus: string) => {
    setSuccess(null);
    if (newStatus === currentStatus) return;
    if(newStatus === 'JUNK' && !isAdmin){
      setError('Only Admin users can set the status to JUNK.');
      return;
    }

    if(newStatus === 'CLOSED' && !isAdmin){
      setError('Only Admin users can set the status to CLOSED.');
      return;
    }
    
    // Check if trying to change from PENDING to CONFIRMED without a vehicle assigned
    if (newStatus === 'CONFIRMED' && !hasVehicleAssigned) {
      setError('Cannot change status to CONFIRMED. Please assign a vehicle first.');
      return;
    }

     // Prevent changing from DRIVER ASSIGNED to CONFIRMED if driver is still assigned
    if (currentStatus === 'DRIVER ASSIGNED' && newStatus === 'CONFIRMED' && hasDriverAssigned) {
      setError('Cannot change status to CONFIRMED while driver is assigned. Please remove the driver first.');
      return;
    }

    // Check if trying to change from DRIVER ASSIGNED to IN PROGRESS before 3 hours
    if (currentStatus === 'DRIVER ASSIGNED' && newStatus === 'IN PROGRESS') {
      if (!isWithinThreeHoursOfPickup()) {
        setError(`Cannot change status to IN PROGRESS. You can only start the trip within 3 hours of pickup time.`);
        return;
      }
    }

    if(newStatus === 'CLOSED'){
      
      const hasCashWithDriverOrOperator = transactions.some(payment => 
        payment.cashInHand === 'DRIVER' ||
        payment.cashInHand === 'OPERATOR'
      );
      
      if (hasCashWithDriverOrOperator) {
        setError('Cannot change status to CLOSED. Some cash payments are still with the driver/operator. Please mark all cash is received by admin before closing the trip.');
        return;
      }

      if (amountDue > 0 && !hasFeedback) {
        setError(`Cannot change status to CLOSED. Outstanding amount due and no feedback provided. Please complete all payments and add feedback before closing the trip.`);
        return;
      }
      if (amountDue > 0) {
        setError(`Cannot change status to CLOSED. Outstanding amount due. Please complete all payments before closing the trip.`);
        return;
      }
      if (!hasFeedback) {
        setError(`Cannot change status to CLOSED. No feedback provided. Please add feedback before closing the trip.`);
        return;
      }
    }

    // Prevent changing from CONFIRMED to DRIVER ASSIGNED if no driver is assigned
    if (currentStatus === 'CONFIRMED' && newStatus === 'DRIVER ASSIGNED' && !hasDriverAssigned) {
      setError('Cannot change status to DRIVER ASSIGNED. Please assign a driver first.');
      return;
    }

    if (newStatus === 'IN CANCELLATION' && currentStatus === 'PENDING') {
      const hasSuccessfulOrRefundedPayments = transactions.some(payment =>
        payment.paymentStatus === 'SUCCESS' || payment.paymentStatus === 'REFUNDED'
      );
      if (!hasSuccessfulOrRefundedPayments) {
      setError("Cannot change status to In Cancellation. This trip has no successful payments.");
      return;
      }
    }

     // Check if trying to change from PENDING to HOT LEAD or COLD LEAD with successful payments
    if (currentStatus === 'PENDING' && ['HOT LEAD', 'COLD LEAD'].includes(newStatus)) {

      const hasPayments = transactions.some(payment =>
        payment.paymentStatus === 'SUCCESS'||
        payment.paymentStatus === 'REFUNDED'
      );

          // Only allow status change if there are no successful or refunded payments
      if (hasPayments) {
        setError(`Cannot change status to ${newStatus}. This trip has payments.`);
        return;
      }
    }
    
    // Special handling for IN CANCELLATION - delegate to parent for confirmation
    if (newStatus === 'IN CANCELLATION') {
      onStatusUpdate?.(newStatus);
      return;
    }

    setIsUpdating(newStatus);
    setError(null);
    setSuccess(null);

    try {
      await api.updateTripStatus(tripCode, newStatus);

      if (newStatus === 'CLOSED') {
        try {
          const response = await api.createZohoInvoice(tripCode);
          if (!response || (response as any)?.data == null) {
            setZohoErrorDialogOpen(true)
          } else {
            
            if(onRefreshTripDetails){
              await onRefreshTripDetails();
            }
            setSuccess(`Trip status updated to ${newStatus} successfully! and Zoho invoice created.`);
          }
        } catch (error) {
          console.error('Error creating Zoho invoice upon closing trip:', error);
          setZohoErrorDialogOpen(true);
        }
      } else {
        setSuccess(`Trip status updated to ${newStatus} successfully!`);
      }
      onStatusUpdate?.(newStatus);
    } catch (error: any) {
      console.error('Error updating trip status:', error);
      setError(error.message || 'Failed to update trip status. Please try again.');
    } finally {
      setIsUpdating(null);
    }
  };
  const handleCloseInvoiceErrorDialog = () => {
    setZohoErrorDialogOpen(false);
    setSuccess('Trip status updated to CLOSED successfully.');
  };
  const handleSendInvoice = async () => {
    setIsSendingInvoice(true);
    setError(null);
    setSuccess(null);
    
    try {
      await onSendInvoice?.();
      setSuccess('Invoice sent successfully!');
    } catch (error: any) {
      console.error('Error sending invoice:', error);
      setError(error.message || 'Failed to send invoice. Please try again.');
    } finally {
      setIsSendingInvoice(false);
    }
  };

  const handleGenerateInvoice = async () => {
    setIsGeneratingInvoice(true);
    setError(null);
    setSuccess(null);
    
    try {
      
      const response = await api.generateTripInvoice(tripCode);
      // Extract and store the invoiceId for later use
      const invoiceId = (response as any)?.data?.invoiceId || 
                       (response as any)?.invoiceId || 
                       (response as any)?.id;
      
      if (invoiceId) {
        setGeneratedInvoiceId(invoiceId);
        
      }   
      setSuccess('Invoice generated successfully!');
      
      // Call the optional callback if provided
      await onGenerateInvoice?.();
    } catch (error: any) {
      console.error('Error generating invoice:', error);
      setError(error.message || 'Failed to generate invoice. Please try again.');
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleDownloadInvoicePdf = async () => {
    setIsDownloadingPdf(true);
    setError(null);
    setSuccess(null);
    
    try { 
      let invoiceId = generatedInvoiceId;   
      // If no invoice has been generated yet, generate it first
      if (!invoiceId) {
        
        const invoiceResponse = await api.generateTripInvoice(tripCode); 
        invoiceId = (invoiceResponse as any)?.data?.invoiceId || 
                   (invoiceResponse as any)?.invoiceId || 
                   (invoiceResponse as any)?.id;
        
        if (!invoiceId) {
          throw new Error('Invoice ID not found in response. Cannot download PDF.');
        }
        // Store it for future use
        setGeneratedInvoiceId(invoiceId);
      } 
      // Download the PDF
      const pdfBlob = await api.downloadInvoicePdf(invoiceId);
      // Create download link and trigger download
      const url = window.URL.createObjectURL(pdfBlob as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${tripCode}-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSuccess('Invoice PDF downloaded successfully!');
      
      // Call the optional callback if provided
      await onDownloadInvoicePdf?.();
    } catch (error: any) {
      console.error('Error downloading invoice PDF:', error);
      setError(error.message || 'Failed to download invoice PDF. Please try again.');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const currentStatusInfo = getCurrentStatusInfo();

  // Define allowed statuses from PENDING
  const getAllowedStatuses = () => {

    if(isAccountant){
      if (currentStatus === 'CLOSED') {
      return STATUS_OPTIONS.filter(status => 
        status.value === 'CLOSED' || status.value === 'AUDITED'
      )
    }
    }

    if(currentStatus === 'AUDITED'){
      return STATUS_OPTIONS.filter(status => 
        status.value === 'AUDITED'
      );
    }

    if (currentStatus === 'PENDING') {
      const allowedFromPending = ['HOT LEAD', 'COLD LEAD', 'CONFIRMED','IN CANCELLATION', 'JUNK'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromPending.includes(status.value) || status.value === currentStatus
      );
    }

    // Define allowed statuses from COLD LEAD
    if (currentStatus === 'COLD LEAD') {
      const allowedFromColdLead = ['HOT LEAD', 'PENDING', 'CONFIRMED', 'JUNK'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromColdLead.includes(status.value) || status.value === currentStatus
      );
    }
    // Define allowed statuses from HOT LEAD
    if (currentStatus === 'HOT LEAD') {
      const allowedFromColdLead = ['COLD LEAD', 'PENDING', 'CONFIRMED', 'JUNK'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromColdLead.includes(status.value) || status.value === currentStatus
      );
    }
    // Define allowed statuses from CONFIRMED
    if (currentStatus === 'CONFIRMED') {
      const allowedFromColdLead = ['DRIVER ASSIGNED','IN CANCELLATION','JUNK'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromColdLead.includes(status.value) || status.value === currentStatus
      );
    }
    // Define allowed statuses from DRIVER ASSIGNED
    if (currentStatus === 'DRIVER ASSIGNED') {
      const allowedFromColdLead = ['CONFIRMED','IN PROGRESS','IN CANCELLATION','JUNK'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromColdLead.includes(status.value) || status.value === currentStatus
      );
    }
    // Define allowed statuses from IN PROGRESS
    if (currentStatus === 'IN PROGRESS') {
      const allowedFromColdLead = ['COMPLETED','IN CANCELLATION','JUNK'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromColdLead.includes(status.value) || status.value === currentStatus
      );
    }
    // Define allowed statuses from IN CANCELLATION
    if (currentStatus === 'IN CANCELLATION') {
      const allowedFromColdLead = ['PENDING','JUNK'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromColdLead.includes(status.value) || status.value === currentStatus
      );
    }
    // Define allowed statuses from COMPLETED
    if (currentStatus === 'COMPLETED') {
      const allowedFromColdLead = ['CLOSED'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromColdLead.includes(status.value) || status.value === currentStatus
      );
    }
    // Define allowed statuses from CLOSED
    if (currentStatus === 'CLOSED') {
      const allowedFromColdLead = ['CLOSED'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromColdLead.includes(status.value) || status.value === currentStatus
      );
    }

    //Define allowed statuses from AUDITED
    if(currentStatus === 'AUDITED'){
      const allowedFromAudited = ['AUDITED'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromAudited.includes(status.value) || status.value === currentStatus
      );
    }

    // Define allowed statuses from JUNK
    if (currentStatus === 'JUNK') {
      const allowedFromColdLead = ['JUNK'];
      return STATUS_OPTIONS.filter(status => 
        allowedFromColdLead.includes(status.value) || status.value === currentStatus
      );
    }
    return STATUS_OPTIONS;
  };

  const allowedStatuses = getAllowedStatuses();

  return (
    <Card sx={cardSx}>
      {/* Header Section */}
      <Box sx={headerSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimelineIcon />
          <Typography variant="h6" fontWeight="bold">
          Trip Details - #{tripCode}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Generate Invoice Button */}
          <Tooltip title="Generate Invoice">
            <Button
              variant="contained"
              size="small"
              startIcon={isGeneratingInvoice ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice || loading}
              sx={buttonGenerateInvoiceSx}
            >
              {isGeneratingInvoice ? 'Generating...' : 'Generate Invoice'}
            </Button>
          </Tooltip>

          {/* Download PDF Invoice Button */}
          <Tooltip title={generatedInvoiceId ? "Download PDF Invoice" : "Generate & Download PDF Invoice"}>
            <Button
              variant="contained"
              size="small"
              startIcon={isDownloadingPdf ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadInvoicePdf}
              disabled={isDownloadingPdf || loading}
              sx={buttonDownloadPdfSx(!!generatedInvoiceId)}
            >
              {isDownloadingPdf ? 'Downloading...' : generatedInvoiceId ? 'Download PDF' : 'Generate & Download PDF'}
            </Button>
          </Tooltip>

          {/* Send Invoice Button */}
          <Tooltip title="Send Invoice">
            <Button
              variant="contained"
              size="small"
              startIcon={isSendingInvoice ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}
              onClick={handleSendInvoice}
              // disabled={isSendingInvoice || loading}
              disabled
              sx={buttonSendInvoiceSx}
            >
              {isSendingInvoice ? 'Sending...' : 'Send Invoice'}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      <CardContent sx={cardContentSx}>
        {error && (
          <Alert 
            severity="error" 
            sx={alertErrorSx}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={alertSuccessSx}
          >
            {success}
          </Alert>
        )}

        {/* Current Status Display */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Current Status
          </Typography>
          <Box 
            sx={statusBoxSx}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Chip
                label={currentStatusInfo.label}
                color={currentStatusInfo.color}
                variant="filled"
                size="medium"
                sx={{ 
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              Last updated: {updatedAt ? formatDateTime(updatedAt) : '-'}
            </Typography>
          </Box>
        </Box>

        {/* Clickable Status Progress */}
        {!disabled &&(
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Update Status (Click to change)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {allowedStatuses.map((status) => {
              const isCurrent = status.value === currentStatus;
              const isUpdatingThis = isUpdating === status.value;
              
              return (
                <Tooltip 
                  key={status.value} 
                  // title={tooltipTitle}
                  title={isCurrent ? 'Current Status' : `Click to change to ${status.label}`}

                >
                  <Box>
                    <Chip
                      label={
                        isUpdatingThis ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={12} color="inherit" />
                            <span>Updating...</span>
                          </Box>
                        ) : (
                          status.label
                        )
                      }
                      size="medium"
                      variant={isCurrent ? "filled" : "outlined"}
                      color={isCurrent ? status.color : "default"}
                      disabled={isUpdatingThis || loading }
                      clickable={!isCurrent}
                      onClick={() => !isCurrent && handleStatusUpdate(status.value)}
                      sx={chipSx(isCurrent)}
                    />
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>)}

        {loading && (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            gap={2} 
            mt={2}
            p={2}
            sx={loadingBoxSx}
          >
            <CircularProgress size={20} sx={{ color: '#0891b2' }} />
            <Typography variant="body2" color="textSecondary">
              Loading trip status...
            </Typography>
          </Box>
        )}
      </CardContent>
      <Dialog
        open={zohoErrorDialogOpen}
        onClose={handleCloseInvoiceErrorDialog}
        aria-labelledby="zoho-invoice-error-dialog-title"
        PaperProps={{
          sx: {
            backgroundColor: 'white',
            padding: 2
          }
        }}
      > 
      <DialogTitle id="invoice-error-dialog">Invoice Creation Failed</DialogTitle>
        <DialogContent>
          <DialogContentText color="error">
            Trip has been closed, but the Zoho invoice could not be generated. Please reach out to tech support.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleCloseInvoiceErrorDialog} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TripStatusSection;

